import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { getProductReviews, addProductReview } from '../../../service/ShopService';
import styles from './ProductReviews.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faStar, 
  faThumbsUp, 
  faThumbsDown, 
  faReply,
  faUser,
  faCalendarAlt,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';

const ProductReviews = ({ product }) => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newReview, setNewReview] = useState({
    rating: 5,
    title: '',
    content: '',
    images: []
  });

  const [showAddReview, setShowAddReview] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (product?.slug) {
      loadReviews();
    }
  }, [product?.slug]);

  const loadReviews = async () => {
    try {
      setLoading(true);
      const response = await getProductReviews(product.slug);
      setReviews(response.data.results || response.data || []);
    } catch (error) {
      console.error('Error loading reviews:', error);
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      alert('Войдите в аккаунт, чтобы оставить отзыв');
      return;
    }

    if (!newReview.content.trim()) {
      alert('Пожалуйста, напишите текст отзыва');
      return;
    }

    setSubmitting(true);
    try {
      await addProductReview(product.slug, {
        rating: newReview.rating,
        title: newReview.title,
        body: newReview.content
      });

      // Reload reviews
      await loadReviews();
      
      setNewReview({ rating: 5, title: '', content: '', images: [] });
      setShowAddReview(false);
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Ошибка при отправке отзыва');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLike = (reviewId, isLike) => {
    setReviews(reviews.map(review => {
      if (review.id === reviewId) {
        if (isLike) {
          return { ...review, likes: review.likes + 1 };
        } else {
          return { ...review, dislikes: review.dislikes + 1 };
        }
      }
      return review;
    }));
  };

  const renderStars = (rating, interactive = false, onClick = null) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <FontAwesomeIcon
          key={i}
          icon={faStar}
          className={`${styles.star} ${i <= rating ? styles.filled : styles.empty} ${interactive ? styles.interactive : ''}`}
          onClick={() => interactive && onClick && onClick(i)}
        />
      );
    }
    return stars;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getAverageRating = () => {
    if (reviews.length === 0) return 0;
    const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
    return (sum / reviews.length).toFixed(1);
  };

  const getRatingDistribution = () => {
    const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach(review => {
      distribution[review.rating]++;
    });
    return distribution;
  };

  const sortedReviews = [...reviews].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.created_at) - new Date(a.created_at);
      case 'oldest':
        return new Date(a.created_at) - new Date(b.created_at);
      case 'highest':
        return b.rating - a.rating;
      case 'lowest':
        return a.rating - b.rating;
      case 'helpful':
        return (b.likes || 0) - (a.likes || 0);
      default:
        return 0;
    }
  });

  const ratingDistribution = getRatingDistribution();

  return (
    <div className={styles.reviewsContainer}>
      {/* Сводка по отзывам */}
      <div className={styles.reviewsSummary}>
        <div className={styles.averageRating}>
          <div className={styles.ratingNumber}>{getAverageRating()}</div>
          <div className={styles.stars}>
            {renderStars(parseFloat(getAverageRating()))}
          </div>
          <div className={styles.totalReviews}>
            {reviews.length} отзывов
          </div>
        </div>

        <div className={styles.ratingDistribution}>
          {[5, 4, 3, 2, 1].map(rating => (
            <div key={rating} className={styles.ratingRow}>
              <span className={styles.ratingLabel}>{rating}</span>
              <FontAwesomeIcon icon={faStar} className={styles.starIcon} />
              <div className={styles.ratingBar}>
                <div 
                  className={styles.ratingFill}
                  style={{ 
                    width: reviews.length > 0 
                      ? `${(ratingDistribution[rating] / reviews.length) * 100}%` 
                      : '0%' 
                  }}
                />
              </div>
              <span className={styles.ratingCount}>
                {ratingDistribution[rating]}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Кнопка добавления отзыва и сортировка */}
      <div className={styles.reviewsHeader}>
        <div className={styles.sortControls}>
          <label>Сортировка:</label>
          <select 
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value)}
            className={styles.sortSelect}
          >
            <option value="newest">Сначала новые</option>
            <option value="oldest">Сначала старые</option>
            <option value="highest">Высокий рейтинг</option>
            <option value="lowest">Низкий рейтинг</option>
            <option value="helpful">Самые полезные</option>
          </select>
        </div>

        <button 
          className={styles.addReviewButton}
          onClick={() => setShowAddReview(true)}
        >
          Написать отзыв
        </button>
      </div>

      {/* Форма добавления отзыва */}
      {showAddReview && (
        <div className={styles.addReviewForm}>
          <form onSubmit={handleReviewSubmit}>
            <h3>Написать отзыв</h3>
            
            <div className={styles.formGroup}>
              <label>Оценка:</label>
              <div className={styles.ratingInput}>
                {renderStars(newReview.rating, true, (rating) => 
                  setNewReview({ ...newReview, rating })
                )}
              </div>
            </div>

            <div className={styles.formGroup}>
              <label>Заголовок:</label>
              <input
                type="text"
                value={newReview.title}
                onChange={(e) => setNewReview({ ...newReview, title: e.target.value })}
                placeholder="Краткое описание вашего впечатления"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label>Отзыв:</label>
              <textarea
                value={newReview.content}
                onChange={(e) => setNewReview({ ...newReview, content: e.target.value })}
                placeholder="Расскажите подробнее о вашем опыте использования товара"
                rows="5"
                required
              />
            </div>

            <div className={styles.formActions}>
              <button type="submit" className={styles.submitButton} disabled={submitting}>
                {submitting ? 'Отправка...' : 'Опубликовать отзыв'}
              </button>
              <button 
                type="button" 
                className={styles.cancelButton}
                onClick={() => setShowAddReview(false)}
                disabled={submitting}
              >
                Отмена
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Список отзывов */}
      <div className={styles.reviewsList}>
        {loading ? (
          <div className={styles.loading}>
            <p>Загрузка отзывов...</p>
          </div>
        ) : sortedReviews.length === 0 ? (
          <div className={styles.noReviews}>
            <p>Пока нет отзывов. Будьте первым, кто оставит отзыв!</p>
          </div>
        ) : (
          sortedReviews.map(review => (
            <div key={review.id} className={styles.review}>
              <div className={styles.reviewHeader}>
                <div className={styles.userInfo}>
                  <img 
                    src={review.user?.avatar || '/api/placeholder/40/40'} 
                    alt={review.user?.username || 'Пользователь'}
                    className={styles.userAvatar}
                  />
                  <div className={styles.userDetails}>
                    <div className={styles.userName}>
                      {review.user?.username || 'Пользователь'}
                      {review.is_verified && (
                        <FontAwesomeIcon 
                          icon={faCheckCircle} 
                          className={styles.verifiedIcon}
                          title="Проверенный покупатель"
                        />
                      )}
                    </div>
                    <div className={styles.reviewDate}>
                      <FontAwesomeIcon icon={faCalendarAlt} />
                      {formatDate(review.created_at)}
                    </div>
                  </div>
                </div>
                <div className={styles.reviewRating}>
                  {renderStars(review.rating)}
                </div>
              </div>

              <div className={styles.reviewContent}>
                {review.title && <h4 className={styles.reviewTitle}>{review.title}</h4>}
                <p className={styles.reviewText}>{review.body || review.content}</p>
              </div>

              <div className={styles.reviewActions}>
                <div className={styles.helpfulActions}>
                  <button 
                    className={styles.helpfulButton}
                    onClick={() => handleLike(review.id, true)}
                  >
                    <FontAwesomeIcon icon={faThumbsUp} />
                    Полезно ({review.likes || 0})
                  </button>
                  <button 
                    className={styles.helpfulButton}
                    onClick={() => handleLike(review.id, false)}
                  >
                    <FontAwesomeIcon icon={faThumbsDown} />
                    ({review.dislikes || 0})
                  </button>
                </div>
                <button className={styles.replyButton}>
                  <FontAwesomeIcon icon={faReply} />
                  Ответить
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ProductReviews;